import { Box, Typography } from "@mui/material"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"

import { ADJUSTMENT_REASON_TO_LABEL } from "../../constants"

import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import { DOCUMENT_UPLOAD_MIME_TYPES, DOCUMENT_UPLOAD_TYPES_LABEL } from "@/components/upload/constants"
import FileInput from "@/components/upload/FileInput"
import { InventoryAdjustmentReason } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface ReasonLabelProps {
  reason: string
  description: string
}

const ReasonLabel: FC<ReasonLabelProps> = ({ reason, description }) => (
  <>
    {reason}
    <span className='ml-1 text-gray-600'>({description})</span>
  </>
)

export const ADJUSTMENT_REASON_OPTIONS: Option<string>[] = [
  {
    value: InventoryAdjustmentReason.Damage,
    label: ADJUSTMENT_REASON_TO_LABEL[InventoryAdjustmentReason.Damage],
    customLabel: <ReasonLabel reason='Damage' description="Material can't be sold" />,
  },
  {
    value: InventoryAdjustmentReason.Samples,
    label: ADJUSTMENT_REASON_TO_LABEL[InventoryAdjustmentReason.Samples],
    customLabel: <ReasonLabel reason='Samples' description='Sent to sample room' />,
  },
  {
    value: InventoryAdjustmentReason.TollMixing,
    label: ADJUSTMENT_REASON_TO_LABEL[InventoryAdjustmentReason.TollMixing],
    customLabel: <ReasonLabel reason='Toll Mixing' description='Sent to manufacturer' />,
  },
  {
    value: InventoryAdjustmentReason.LostMaterial,
    label: ADJUSTMENT_REASON_TO_LABEL[InventoryAdjustmentReason.LostMaterial],
    customLabel: <ReasonLabel reason='Lost Material' description="Missing / can't locate" />,
  },
  {
    value: InventoryAdjustmentReason.FoundMaterial,
    label: ADJUSTMENT_REASON_TO_LABEL[InventoryAdjustmentReason.FoundMaterial],
    customLabel: <ReasonLabel reason='Found Material' description='Not previously in system' />,
  },
  {
    value: InventoryAdjustmentReason.CustomerReturn,
    label: ADJUSTMENT_REASON_TO_LABEL[InventoryAdjustmentReason.CustomerReturn],
    customLabel: <ReasonLabel reason='Customer Return' description='Initiates receiving' />,
  },
  {
    value: InventoryAdjustmentReason.ExpiredMaterial,
    label: ADJUSTMENT_REASON_TO_LABEL[InventoryAdjustmentReason.ExpiredMaterial],
    customLabel: <ReasonLabel reason='Expired Material' description='Shelf life exceeded' />,
  },
  { value: InventoryAdjustmentReason.Other, label: "Other" },
]

export const QuantityAdjustmentFormDetails: FC = () => {
  const { control } = useFormContext()

  return (
    <Box className='flex flex-col overflow-hidden rounded-lg border border-gray-400'>
      <Typography variant='h6' className='p-4 font-bold leading-10'>
        3. Adjustment Details
      </Typography>
      <Box className='flex min-h-0 flex-1 flex-col gap-y-6 overflow-y-auto px-4 pb-4'>
        <FormSelect
          control={control}
          name='adjustmentReason'
          label='Adjustment Reason'
          required
          placeholder='Select one'
          options={ADJUSTMENT_REASON_OPTIONS}
          MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
          data-testid='inventory.quantity-adjustment-form-details.reason-field'
        />
        <FormTextField
          control={control}
          name='comment'
          label='Comment'
          placeholder='Enter comment'
          multiline
          rows={4}
          data-testid='inventory.quantity-adjustment-form-details.comment-field'
        />
        <FileInput
          name='document'
          allowedMimeTypes={DOCUMENT_UPLOAD_MIME_TYPES}
          allowedTypesLabel={DOCUMENT_UPLOAD_TYPES_LABEL}
          data-testid='inventory.quantity-adjustment-form-details.document-field'
        />
      </Box>
    </Box>
  )
}
